import { useEffect, useState } from "react";

/* ─────────────────────────────────────────────
   IMAGE IMPORTS — src/assets/images/blogs/
───────────────────────────────────────────── */
import HeroImg             from "../../assets/images/blogs/electrical-infrastructure-Detail.webp";
import HeroImgMobile       from "../../assets/images/blogs/electrical-infrastructure-mobile.webp";
import NewsletterBgDesktop from "../../assets/images/blogs/newsletter-bg.webp";
import NewsletterMicMobile from "../../assets/images/blogs/newsletter-bg-mobile.webp";

/* ─────────────────────────────────────────────
   TheSurgeInElectrificationOfTransportationRequiresASustainableAndResilientElectricalInfrastructure.jsx
   Prefix      : se-
   Fonts       : Montserrat (headings) | Roboto (body)
   Purple      : #743FAC
   BG main     : #1A1A1A
   Inner pad   : 102px desktop | 32px tab | 16px mobile
   Breaks      : 768px | 480px
   Anim        : IntersectionObserver fade-up
                 delays 0.08s / 0.18s / 0.28s / 0.38s

   DIFF vs other pages:
   - Hero title  : CENTERED (3 lines)
   - se-link     : underlined inline hyperlink (purple)
   - se-subhead--bold : bold sentence as subhead (no margin-top like others)
   - se-bullet   : • regular bullet list item
   - No tweet images, no inline images
   - Newsletter  : prop-based isMobile, Subscribe button
─────────────────────────────────────────────── */

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&family=Roboto:wght@300;400;500;700&display=swap');

  .se-wrap *, .se-wrap *::before, .se-wrap *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  .se-wrap {
    font-family: 'Roboto', sans-serif;
    background: #1A1A1A;
    color: #ffffff;
    width: 100%;
    overflow-x: hidden;
  }
  .se-inner {
    max-width: 1600px;
    margin: 0 auto;
    padding-left: 102px;
    padding-right: 102px;
    width: 100%;
  }
  .se-content-col { width: 100%; }

  /* ── SCROLL FADE-UP ── */
  .se-fade-up {
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .se-fade-up.se-in { opacity: 1; transform: translateY(0); }
  .se-delay-1 { transition-delay: 0.08s; }
  .se-delay-2 { transition-delay: 0.18s; }
  .se-delay-3 { transition-delay: 0.28s; }
  .se-delay-4 { transition-delay: 0.38s; }

  /* ══════════════════════════════════════
     HERO — centered title
  ══════════════════════════════════════ */
  .se-hero { width: 100%; background: #1A1A1A; }
  .se-hero__title-bar {
    width: 100%;
    background: #1A1A1A;
    padding: 60px 0px;
  }
  .se-hero__title {
    font-family: 'Montserrat', sans-serif;
    font-size: 50px;
    font-weight: 700;
    color: #ffffff;
    line-height: 140%;
    text-align: center;
  }
  .se-hero__img-wrap {
    width: 100%;
    max-height: 600px;
    overflow: hidden;
  }
  .se-hero__img-wrap img {
    width: 100%;
    height: 100%;
    max-height: 600px;
    object-fit: cover;
    object-position: center;
    display: block;
  }

  /* ══════════════════════════════════════
     BODY CONTENT
  ══════════════════════════════════════ */
  .se-body-section {
    width: 100%;
    background: #1A1A1A;
    padding: 60px 0 100px;
  }

  /* regular paragraph */
  .se-para {
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 160%;
    color: #E2E2E2;
    margin-bottom: 24px;
  }
        .se-para:last-child {  margin-bottom: 0px;}

  /* inline underlined hyperlink */
  .se-link {
    color: #E2E2E2;
    text-decoration: underline;
    text-underline-offset: 2px;
  }
  .se-link:hover {
    color: #9b59f5;
  }

  /* bold subhead sentence — "There are several solutions..." */
  .se-subhead--bold {
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 700;
    line-height: 160%;
    color: #ffffff;
    margin-bottom: 24px;
  }

  /* bullet list wrapper */
  .se-list {
    margin-bottom: 24px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  /* • regular bullet item */
  .se-bullet {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 160%;
    color: #E2E2E2;
  }
  .se-bullet::before {
    content: '•';
    flex-shrink: 0;
    font-size: 16px;
    line-height: 160%;
    color: #E2E2E2;
  }

  /* ══════════════════════════════════════
     NEWSLETTER
  ══════════════════════════════════════ */
  .se-newsletter {
    width: 100%;
    position: relative;
    overflow: hidden;
    min-height: 500px;
    display: flex;
    align-items: center;
  }
  .se-newsletter__bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
   
    object-position: center;
    z-index: 0;
  }
  .se-newsletter__overlay {
    position: absolute;
    inset: 0;
    z-index: 1;
  }
  .se-newsletter__inner {
    position: relative;
    z-index: 2;
    width: 100%;
    max-width: 1600px;
    margin: 0 auto;
    padding: 0px 102px;
  }
  .se-newsletter__heading {
    font-family: 'Montserrat', sans-serif;
    font-size: 50px;
    font-weight: 700;
    color: #ffffff;
    line-height: 120%;
    margin-bottom: 14px;
  }
  .se-newsletter__sub {
    font-family: 'Roboto', sans-serif;
    font-size: 15px;
    color: rgba(255,255,255,0.72);
    line-height: 160%;
    margin-bottom: 24px;
  }
  .se-newsletter__form {
    display: flex;
    align-items: stretch;
    max-width: 552px;
    gap: 14px;
  }
  .se-newsletter__input {
    flex: 1;
    height: 48px;
    background: #1A1A1A;
    border: 1px solid #ffffff;
    border-radius: 5px;
    padding: 15px 14px;
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    color: #FFFFFF;
    outline: none;
  }
  .se-newsletter__input::placeholder { color: #FFFFFF; }
  .se-newsletter__btn {
    height: 50px;
    padding: 15px 30px;
    background: #743FAC;
    border: none;
    border-radius: 5px;
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 500;
    color: #ffffff;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
    transition: background 0.25s ease;
  }
  .se-newsletter__btn:hover { background: #5e2e90; }
  .se-newsletter__btn-arrow {
    display: inline-flex;
    align-items: center;
    transition: transform 0.2s ease;
  }
  .se-newsletter__btn:hover .se-newsletter__btn-arrow { transform: translateX(4px); }
  .se-newsletter__mic-img {
    width: 100%;
    height: auto;
    display: block;
    object-fit: cover;
  }
  .se-newsletter__content { padding: 0; }

  /* ══════════════════════════
     RESPONSIVE 768px
  ══════════════════════════ */
  @media (max-width: 768px) {
    .se-inner { padding-left: 32px; padding-right: 32px; }
    .se-hero__img-wrap img { max-height: 350px; }
    .se-hero__title-bar { padding: 24px 0px 20px; }
    .se-hero__title { font-size: 24px; }
    .se-body-section { padding: 40px 0 48px; }
    .se-para { margin-bottom: 18px; font-size: 15px; }
    .se-subhead--bold { font-size: 15px; }
    .se-bullet { font-size: 15px; }

    .se-newsletter {
      min-height: auto;
      align-items: stretch;
      flex-direction: column;
      background: #1A1A1A;
    }
    .se-newsletter__mic-img { width: 100%; height: 350px; object-fit: cover; }
    .se-newsletter__inner {
      padding: 0;
      display: flex;
      flex-direction: column;
    }
    .se-newsletter__content {
      padding: 40px 24px 48px;
      text-align: center;
    }
    .se-newsletter__heading { font-size: 24px; }
    .se-newsletter__sub { font-size: 14px; margin: 0 auto 24px; }
    .se-newsletter__form {
      flex-direction: column;
      max-width: 100%;
      gap: 12px;
    }
    .se-newsletter__input { border-radius: 5px; height: 44px; }
    .se-newsletter__btn {
      border-radius: 5px;
      justify-content: center;
      height: 44px;
    }
  }

  /* ══════════════════════════
     RESPONSIVE 480px
  ══════════════════════════ */
  @media (max-width: 480px) {
    .se-inner { padding-left: 16px; padding-right: 16px; }
    .se-hero__title-bar { padding: 30px 0px; }
    .se-body-section { padding: 30px 0 30px; }
    .se-hero__img-wrap img { max-height: 366px; width: 100%; }
    .se-hero__title { font-size: 26px; line-height: 120%; }
    .se-para { font-size: 14px; margin-bottom: 14px; }
    .se-subhead--bold { font-size: 14px;  margin-bottom: 14px;}
    .se-bullet { font-size: 14px; }

    .se-newsletter {
      min-height: auto;
      align-items: stretch;
      flex-direction: column;
      background: #1A1A1A;
    }
    .se-newsletter__mic-img { width: 100%; height: 366px; object-fit: cover; }
    .se-newsletter__inner {
      padding: 0;
      display: flex;
      flex-direction: column;
    }
    .se-newsletter__content {
      padding: 30px 16px 40px;
      text-align: center;
    }
    .se-newsletter__heading { font-size: 30px; line-height: 120%; }
    .se-newsletter__sub { font-size: 14px; max-width: 275px; margin: 0 auto 24px; }
    .se-newsletter__form {
      flex-direction: column;
      max-width: 100%;
      gap: 15px;
      margin-top: 15px;
    }
    .se-newsletter__input { border-radius: 5px; height: 40px; font-size: 14px; }
    .se-newsletter__btn {
      border-radius: 5px;
      justify-content: center;
      height: 40px;
      font-size: 14px;
    }
  }
`;

/* ── SCROLL REVEAL ── */
function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".se-fade-up");
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("se-in"); }),
      { threshold: 0.12 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

/* ── ARROW ── */
function ArrowRight() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

/* ── NEWSLETTER ── */
function Newsletter({ isMobile }) {
  const [email, setEmail] = useState("");
  return (
    <section className="se-newsletter">
      {!isMobile && (
        <>
          <img className="se-newsletter__bg" src={NewsletterBgDesktop} alt="" aria-hidden="true" />
          <div className="se-newsletter__overlay" />
        </>
      )}
      <div className="se-newsletter__inner">
        {isMobile && (
          <img className="se-newsletter__mic-img" src={NewsletterMicMobile} alt="Microphone" />
        )}
        <div className="se-newsletter__content">
          <h2 className="se-newsletter__heading">Sign up for our<br />newsletter!</h2>
          <p className="se-newsletter__sub">Get notified about updates and be the first to get early access to new episodes.</p>
          <div className="se-newsletter__form">
            <input
              className="se-newsletter__input"
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button className="se-newsletter__btn">
              Subscribe
              <span className="se-newsletter__btn-arrow"><ArrowRight /></span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── ROOT EXPORT ── */
export default function TheSurgeInElectrificationOfTransportationRequiresASustainableAndResilientElectricalInfrastructure() {
  useScrollReveal();

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    setIsMobile(mq.matches);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const [isSmallMobile, setIsSmallMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 480px)");
    setIsSmallMobile(mq.matches);
    const handler = (e) => setIsSmallMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return (
    <>
      <style>{CSS}</style>
      <div className="se-wrap">

        {/* HERO — centered title */}
        <section className="se-hero">
          <div className="se-hero__title-bar">
            <div className="se-inner">
              <h1 className="se-hero__title">The Surge in Electrification of Transportation Requires a Sustainable and Resilient Electrical Infrastructure</h1>
            </div>
          </div>
          <div className="se-hero__img-wrap">
            <img
              src={isSmallMobile ? HeroImgMobile : HeroImg}
              alt="Electrical Infrastructure for EV Transportation"
            />
          </div>
        </section>

        {/* BODY */}
        <section className="se-body-section">
          <div className="se-inner">
            <div className="se-content-col">

              {/* image 2 — paras with inline links */}
              <p className="se-para se-fade-up se-delay-1">
                As society contends with the impacts of climate change, we need to aggressively pursue strategies for reducing carbon emissions. Although states in the U.S. are taking a variety of approaches to greenhouse gas (GHG) reduction, and following an equally diverse set of timelines, most state-level GHG emission goals announced so far are fairly consistent—to{" "}
                <a
                  href="https://www.c2es.org/content/state-climate-policy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="se-link"
                >
                  reduce carbon emissions 40%
                </a>{" "}
                by 2030 and 80% by 2050.
              </p>

              <p className="se-para se-fade-up se-delay-1">Achieving these goals will require a variety of major changes, including shifting the entire energy system toward dramatically increased reliance on electricity, particularly the world's transportation, building, and industrial sectors. While this process is already underway, it is still in its early stages, and the changes to come over the next 30 years will be profound; multiple recent studies have indicated that, across the total world energy system, electricity consumption will more than double from 20% today to over 40% of total energy demand by 2050.</p>

              <p className="se-para se-fade-up se-delay-1">
                Among the key drivers for this shift will be the electrification of transportation on a massive scale. Cars, trucks, and other vehicles account for a significant portion of fossil-fuel consumption and approximately 28% of associated GHG emissions. While electric vehicles (EVs) currently make up roughly{" "}
                <a
                  href="https://www.iea.org/reports/global-ev-outlook-2021"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="se-link"
                >
                  2% of the total number of vehicles in the U.S.
                </a>
                , that proportion is certain to increase rapidly as battery prices fall; diversity and overall supply of EVs expands; and consumer demand increases, among other factors.
              </p>

              <p className="se-para se-fade-up se-delay-1">This coming shift is well-recognized in the automotive industry, and we have recently seen major manufacturers announce plans to discontinue production of internal combustion vehicles as soon as 2030. Similarly, virtually every car maker is aggressively pursuing EV strategies. By moving quickly toward the use of EVs, we can eliminate a major source of GHGs, assuming of course that the electricity used to power them comes from clean, renewable sources.</p>

              <p className="se-para se-fade-up se-delay-1">This major shift toward more EVs creates a number of challenges in terms of meeting the growing demand for electricity. According to a National Renewable Energy Laboratory forecast, the transition to EVs will lead to a 20% to 38% increase in electricity consumption by 2050.</p>

              <p className="se-para se-fade-up se-delay-1">In order to achieve 100% carbon neutrality, the additional electricity must come from renewable generation sources. In addition, there needs to be a nationwide plan for upgrading and expanding our electrical grid infrastructure to ensure that it is able to handle the extra load on the grid and integrate additional renewable generation sources.</p>

              <p className="se-para se-fade-up se-delay-1">Most power professionals know that our current electrical transmission and distribution infrastructure was designed and built for the purpose of delivering electricity from centralized, mainly thermal power generation resources. In order to meet the growing demand for electricity and integrate more renewables, we need to prepare a plan for designing future electrical grids. These "EV-proof" grids need to be decentralized, and more interconnected to be able to integrate and transmit large volumes of renewable energy both from distributed renewable generation sources, such as rooftop solar as well as larger-scale wind, solar, hydropower, and other natural energy sources, and be able to handle the additional load.</p>

              {/* image 3 — bold subhead + bullet list + paras */}
              <p className="se-subhead--bold se-fade-up se-delay-1">There are several solutions to solve these challenges. They include:</p>

              <div className="se-list se-fade-up se-delay-2">
                <p className="se-bullet">Building out transmission lines with greater capacity between regions where renewable generation is prevalent to major load centers.</p>
                <p className="se-bullet">Expanding the availability of storage options, such as pumped hydro and advanced battery energy storage systems, to help address time-of-day limitations, grid congestion, and peak demand management, among other challenges.</p>
                <p className="se-bullet">Expanding the network of microgrids, which serve as a reliable backup system in case of failure at the utility level and help communities in remote areas increase power supply reliability, reduce the need for more costly generator fuels, and boost renewable energy production.</p>
              </div>

              <p className="se-para se-fade-up se-delay-1">These challenges will call out for national planning processes for both transportation and electrical infrastructure upgrades that can help to identify and prioritize transmission, storage, and interconnection requirements to support widespread EV use. This process must involve a variety of stakeholders, including federal and state regulators, utilities, grid operators, fleet operators, municipalities, industry associations, electrical infrastructure suppliers, and many others. These groups need to come together to devise plans and set a course that can accelerate this energy transition to achieve our goal of carbon-neutrality by 2050.</p>

              <p className="se-para se-fade-up se-delay-1">With the proper investment and coordination across stakeholder groups, we can work together to accommodate the electrification of society, starting with the rapid transition toward EVs. Through careful planning and the smart application of both existing and emerging technologies, we can not only reduce GHG emissions to address sustainability goals, but also establish a more resilient grid that is better able to respond to the increasingly erratic weather events brought on by climate change.</p>

            </div>
          </div>
        </section>

        {/* NEWSLETTER */}
        <Newsletter isMobile={isMobile} />

      </div>
    </>
  );
}